import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { CheckCircle, AlertCircle, Loader2, XCircle, MapPin, Calendar, FileText, MessageSquare, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { getCustomerDisplayName, formatDate } from "@/lib/formatters";
import { TASK_TYPE_LABELS } from "@/config/constants";

interface OrderData {
  order_number: string;
  notes: string | null;
  installation_date: string | null;
  cemetery_approval_status: string;
  cemetery_approval_notes: string | null;
  cemetery_approval_message: string | null;
  order_lines: Array<{
    description: string;
    quantity: number;
    work_details: { graveNumber?: string; deceasedName?: string } | null;
  }>;
  customers: {
    first_name: string | null;
    last_name: string | null;
    company_name: string | null;
    type: string;
  };
  cemeteries: {
    name: string;
    address: string;
    city: string;
    parish_name: string | null;
  };
  attached_proof_url?: string;
  approved_proof_url?: string;
}

interface InstallationData {
  id: string;
  task_type: string;
  scheduled_date: string | null;
  notes: string | null;
  cemetery_approval_status: string;
  cemetery_approval_notes: string | null;
  orders: {
    order_number: string;
    notes: string | null;
    order_lines: Array<{
      description: string;
      quantity: number;
      work_details: { graveNumber?: string; deceasedName?: string } | null;
    }>;
    customers: {
      first_name: string | null;
      last_name: string | null;
      company_name: string | null;
      type: string;
    };
  };
  cemeteries: {
    name: string;
    address: string;
    city: string;
    parish_name: string | null;
  };
  approved_proof_url?: string;
}

type ApprovalData = 
  | { type: "order"; data: OrderData }
  | { type: "installation"; data: InstallationData };

interface TemplateMap {
  cemetery_page_title?: string;
  cemetery_approved_message?: string;
  cemetery_rejected_message?: string;
}

export default function CemeteryApproval() {
  const { token } = useParams<{ token: string }>();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [approvalData, setApprovalData] = useState<ApprovalData | null>(null);
  const [templates, setTemplates] = useState<TemplateMap>({});
  const [error, setError] = useState<string | null>(null);
  const [rejectionNotes, setRejectionNotes] = useState("");
  const [completed, setCompleted] = useState<"approved" | "rejected" | null>(null);

  useEffect(() => {
    if (token) {
      fetchApprovalData();
    }
  }, [token]);

  const fetchApprovalData = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/cemetery-approval?token=${token}`
      );
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || "Pyyntöä ei löytynyt");
      }

      // Store templates from response
      if (data.templates) {
        setTemplates(data.templates);
      }
      
      if (data.type === "order") {
        setApprovalData({ type: "order", data: data.order });
        
        if (data.order.cemetery_approval_status === "approved") {
          setCompleted("approved");
        } else if (data.order.cemetery_approval_status === "rejected") {
          setCompleted("rejected");
        }
      } else {
        // Installation (backward compatibility)
        setApprovalData({ type: "installation", data: data.installation });
        
        if (data.installation.cemetery_approval_status === "approved") {
          setCompleted("approved");
        } else if (data.installation.cemetery_approval_status === "rejected") {
          setCompleted("rejected");
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Tuntematon virhe");
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (action: "approve" | "reject") => {
    if (action === "reject" && !rejectionNotes.trim()) {
      toast.error("Hylkäyksen syy puuttuu", {
        description: "Kirjoita syy miksi pyyntö hylätään",
      });
      return;
    }

    try {
      setSubmitting(true);
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/cemetery-approval`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            token,
            action,
            notes: action === "reject" ? rejectionNotes : undefined,
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Toiminto epäonnistui");
      }

      setCompleted(action === "approve" ? "approved" : "rejected");
      toast.success(action === "approve" ? "Hyväksytty!" : "Hylätty", {
        description: action === "approve" 
          ? "Kiitos hyväksynnästä. Työ voi jatkua suunnitellusti." 
          : "Ilmoitamme asiasta urakoitsijalle.",
      });
    } catch (err) {
      toast.error("Virhe", {
        description: err instanceof Error ? err.message : "Tuntematon virhe",
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Helper functions to get data regardless of type
  const getOrderNumber = (): string => {
    if (!approvalData) return "";
    if (approvalData.type === "order") {
      return approvalData.data.order_number;
    }
    return approvalData.data.orders.order_number;
  };

  const getCustomerName = (): string => {
    if (!approvalData) return "";
    const customer = approvalData.type === "order" 
      ? approvalData.data.customers 
      : approvalData.data.orders.customers;
    return getCustomerDisplayName(customer);
  };

  const getCemetery = () => {
    if (!approvalData) return null;
    return approvalData.data.cemeteries;
  };

  const getScheduledDate = (): string | null => {
    if (!approvalData) return null;
    if (approvalData.type === "order") {
      return approvalData.data.installation_date;
    }
    return approvalData.data.scheduled_date;
  };

  const getOrderLines = () => {
    if (!approvalData) return [];
    if (approvalData.type === "order") {
      return approvalData.data.order_lines;
    }
    return approvalData.data.orders.order_lines;
  };

  const getNotes = (): string | null => {
    if (!approvalData) return null;
    if (approvalData.type === "order") {
      return approvalData.data.notes;
    }
    return approvalData.data.notes || approvalData.data.orders.notes;
  };

  const getApprovalMessage = (): string | null => {
    if (!approvalData) return null;
    if (approvalData.type === "order") {
      return approvalData.data.cemetery_approval_message;
    }
    return null;
  };

  const getApprovedProofUrl = (): string | undefined => {
    if (!approvalData) return undefined;
    return approvalData.data.approved_proof_url;
  };

  const getAttachedProofUrl = (): string | undefined => {
    if (!approvalData) return undefined;
    if (approvalData.type === "order") {
      return approvalData.data.attached_proof_url;
    }
    return undefined;
  };

  const getTaskType = (): string | null => {
    if (!approvalData) return null;
    if (approvalData.type === "installation") {
      return approvalData.data.task_type;
    }
    return null;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center">
            <Loader2 className="h-12 w-12 animate-spin mx-auto text-primary mb-4" />
            <p className="text-muted-foreground">Ladataan tietoja...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center">
            <XCircle className="h-16 w-16 mx-auto text-destructive mb-4" />
            <h1 className="text-xl font-semibold mb-2">Pyyntöä ei löytynyt</h1>
            <p className="text-muted-foreground">{error}</p>
            <p className="text-sm text-muted-foreground mt-4">
              Linkki on saattanut vanhentua tai pyyntö on jo käsitelty.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (completed) {
    const approvedMessage = templates.cemetery_approved_message || 
      "Kiitos hyväksynnästä. Työ toteutetaan suunnitellusti hautausmaalla.";
    const rejectedMessage = templates.cemetery_rejected_message || 
      "Olemme vastaanottaneet hylkäyksen ja otamme yhteyttä asian selvittämiseksi.";

    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center">
            {completed === "approved" ? (
              <>
                <CheckCircle className="h-16 w-16 mx-auto text-green-500 mb-4" />
                <h1 className="text-xl font-semibold mb-2">Hyväksytty!</h1>
                <p className="text-muted-foreground">
                  {approvedMessage}
                </p>
              </>
            ) : (
              <>
                <AlertCircle className="h-16 w-16 mx-auto text-red-500 mb-4" />
                <h1 className="text-xl font-semibold mb-2">Hylätty</h1>
                <p className="text-muted-foreground">
                  {rejectedMessage}
                </p>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  const cemetery = getCemetery();
  const taskType = getTaskType();

    const pageTitle = templates.cemetery_page_title || "Asennuksen hyväksyntäpyyntö";

    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8 max-w-4xl">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold mb-2">{pageTitle}</h1>
            <p className="text-muted-foreground">
              Tilaus: <span className="font-medium">{getOrderNumber()}</span>
            </p>
          {cemetery?.parish_name && (
            <p className="text-sm text-muted-foreground">
              Seurakunta: {cemetery.parish_name}
            </p>
          )}
        </div>

        {/* Details */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Työn tiedot
            </CardTitle>
            <CardDescription>
              Tarkista tiedot ja hyväksy tai hylkää pyyntö
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Location */}
            {cemetery && (
              <div className="flex items-start gap-3">
                <MapPin className="h-5 w-5 text-muted-foreground mt-0.5" />
                <div>
                  <p className="font-medium">{cemetery.name}</p>
                  <p className="text-sm text-muted-foreground">
                    {cemetery.address}, {cemetery.city}
                  </p>
                </div>
              </div>
            )}

            {/* Scheduled date */}
            <div className="flex items-start gap-3">
              <Calendar className="h-5 w-5 text-muted-foreground mt-0.5" />
              <div>
                <p className="font-medium">Suunniteltu päivämäärä</p>
                <p className="text-sm text-muted-foreground">
                  {formatDate(getScheduledDate())}
                </p>
              </div>
            </div>

            {/* Task type (only for installation) */}
            {taskType && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Työn tyyppi:</span>
                <Badge variant="outline">
                  {TASK_TYPE_LABELS[taskType] || taskType}
                </Badge>
              </div>
            )}

            {/* Customer */}
            {getCustomerName() && (
              <div>
                <span className="text-sm text-muted-foreground">Asiakas:</span>
                <span className="ml-2 font-medium">{getCustomerName()}</span>
              </div>
            )}

            <Separator className="my-4" />

            {/* Order lines */}
            <div>
              <h4 className="font-medium mb-2">Työt</h4>
              <ul className="space-y-2">
                {getOrderLines().map((line, idx) => (
                  <li key={idx} className="text-sm bg-muted/50 rounded-lg p-3">
                    <p className="font-medium">{line.description}</p>
                    {line.quantity > 1 && (
                      <p className="text-muted-foreground">Määrä: {line.quantity}</p>
                    )}
                    {line.work_details && (
                      <div className="mt-1 text-muted-foreground">
                        {line.work_details.graveNumber && (
                          <p>Haudan numero: {line.work_details.graveNumber}</p>
                        )}
                        {line.work_details.deceasedName && (
                          <p>Vainaja: {line.work_details.deceasedName}</p>
                        )}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </div>

            {/* Sender message */}
            {getApprovalMessage() && (
              <>
                <Separator className="my-4" />
                <div>
                  <h4 className="font-medium mb-2 flex items-center gap-2">
                    <MessageSquare className="h-4 w-4" />
                    Viesti
                  </h4>
                  <div className="bg-blue-50 rounded-lg p-4">
                    <p className="text-sm whitespace-pre-wrap">{getApprovalMessage()}</p>
                  </div>
                </div>
              </>
            )}

            {/* Notes */}
            {getNotes() && (
              <>
                <Separator className="my-4" />
                <div>
                  <h4 className="font-medium mb-2">Lisätiedot</h4>
                  <p className="text-sm text-muted-foreground">{getNotes()}</p>
                </div>
              </>
            )}

            {/* Attached proof image (priority) or approved proof */}
            {(getAttachedProofUrl() || getApprovedProofUrl()) && (
              <>
                <Separator className="my-4" />
                <div>
                  <h4 className="font-medium mb-2 flex items-center gap-2">
                    <ImageIcon className="h-4 w-4" />
                    {getAttachedProofUrl() ? "Liitetty vedos" : "Hyväksytty vedos"}
                  </h4>
                  <div className="relative overflow-hidden rounded-lg border">
                    <img
                      src={getAttachedProofUrl() || getApprovedProofUrl()}
                      alt="Vedos"
                      className="w-full h-auto"
                    />
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Actions */}
        <Card>
          <CardHeader>
            <CardTitle>Hyväksy tai hylkää</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Approve button */}
            <div>
              <Button 
                size="lg" 
                className="w-full bg-green-600 hover:bg-green-700"
                onClick={() => handleAction("approve")}
                disabled={submitting}
              >
                {submitting ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <CheckCircle className="h-4 w-4 mr-2" />
                )}
                Hyväksy
              </Button>
              <p className="text-xs text-center text-muted-foreground mt-2">
                Hyväksymällä vahvistat, että työ voidaan toteuttaa hautausmaalla.
              </p>
            </div>

            {/* Divider */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-border" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-card px-2 text-muted-foreground">tai</span>
              </div>
            </div>

            {/* Rejection */}
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="rejection-notes">Hylkää</Label>
                <Textarea
                  id="rejection-notes"
                  placeholder="Kirjoita tähän syy miksi pyyntö hylätään..."
                  value={rejectionNotes}
                  onChange={(e) => setRejectionNotes(e.target.value)}
                  rows={4}
                />
              </div>
              <Button 
                variant="outline" 
                size="lg"
                className="w-full border-destructive text-destructive hover:bg-destructive/10"
                onClick={() => handleAction("reject")}
                disabled={submitting || !rejectionNotes.trim()}
              >
                {submitting ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <XCircle className="h-4 w-4 mr-2" />
                )}
                Hylkää
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center mt-8 text-xs text-muted-foreground">
          <p>Tämä sivu on luotu automaattisesti hyväksyntäpyyntöä varten.</p>
          <p>Älä jaa tätä linkkiä muille.</p>
        </div>
      </div>
      
    </div>
  );
}
